// WalletView.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Crown } from 'lucide-react';
import { useOrganization } from '../../contexts/OrganizationContext';
import SubscriptionView from './SubscriptionView';
import useAccountBalance from '../../hooks/billing/useAccountBalance';
import useAccountTier from '../../hooks/billing/useAccountTier';
import useUsageSummary from '../../hooks/billing/useUsageSummary';
import useTransactions from '../../hooks/billing/useTransactions';
import BalanceCard from './components/BalanceCard';
import BillingOverviewCard from './components/BillingOverviewCard';
import UsageSummaryPanel from './components/UsageSummaryPanel';
import TransactionFilters from './components/TransactionFilters';
import TransactionsTable from './components/TransactionsTable';
import TransactionRowDrawer from './components/TransactionRowDrawer';
import WalletContentTabs from './components/WalletContentTabs';
import WalletSummaryInsights from './components/WalletSummaryInsights';
import CoinsPurchasePanel from './components/CoinsPurchasePanel';
import './WalletView.css';

/**
 * @param {string}   tab          pestaña activa; vive en la URL (?tab=) de /accounts/:id/billing
 * @param {Function} onTabChange  (tab) => void — quien monta la vista escribe la URL
 */
export default function WalletView({ tab, onTabChange, onViewPlans }) {
  const { t } = useTranslation();
  const { currentAccount, currentContext } = useOrganization();
  const accountId = currentAccount?.id ?? null;
  const isOrgContext = currentAccount?.type === 'ORGANIZATION';
  const accountName =
    currentAccount?.name ?? currentAccount?.display_name ?? null;
  const orgName = currentContext?.name ?? null;

  // La pestaña de contenido es controlada: vive en la URL de quien monta la
  // vista. Antes se derivaba de `location.pathname === '/user/coins'` y cambiar
  // de pestaña navegaba, porque "coins" tenía que ser una URL real para que
  // Tilopay pudiera volver. Con la pestaña en `?tab=` sigue siéndolo, y las
  // otras cuatro dejan de necesitar un caso especial.
  const contentTab = tab || 'summary';
  const handleContentTabChange = (next) => onTabChange?.(next);

  const {
    data: balance,
    loading: balanceLoading,
    error: balanceError,
    refetch: refetchBalance,
  } = useAccountBalance(accountId);

  const { data: tier, loading: tierLoading } = useAccountTier(accountId);

  const [usageRange, setUsageRange] = useState({ preset: 'period' });
  const {
    data: usage,
    loading: usageLoading,
    error: usageError,
  } = useUsageSummary(accountId, {
    since: usageRange.since,
    until: usageRange.until,
  });

  // ----- Transactions ledger state ----------------------------------
  const [txnResourceTypes, setTxnResourceTypes] = useState([]);
  const [txnTransactionType, setTxnTransactionType] = useState(null);
  const [txnRange, setTxnRange] = useState({ preset: 'period' });
  const [openTransaction, setOpenTransaction] = useState(null);
  const [hideZeroAmount, setHideZeroAmount] = useState(false);

  const {
    items: transactions,
    loading: transactionsLoading,
    error: transactionsError,
    refetch: refetchTransactions,
    page: transactionsPage,
    hasPrevPage: transactionsHasPrev,
    hasNextPage: transactionsHasNext,
    nextPage: nextTransactionsPage,
    prevPage: prevTransactionsPage,
  } = useTransactions(accountId, {
    resourceTypes: txnResourceTypes.length > 0 ? txnResourceTypes : undefined,
    transactionType: txnTransactionType ?? undefined,
    since: txnRange.since,
    until: txnRange.until,
    excludeZeroAmount: hideZeroAmount,
  });

  const clearTxnFilters = () => {
    setTxnResourceTypes([]);
    setTxnTransactionType(null);
    setTxnRange({ preset: 'period' });
  };

  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refetchBalance();
    } finally {
      setRefreshing(false);
    }
  };

  const isEnterprisePool = Boolean(
    balance?.organization?.isEnterprisePool
  );


  const tierAllowance =
    tier?.monthlyCoinAllowance ?? balance?.tier?.monthlyCoinAllowance ?? null;
  // The allowance progress only reads correctly when the usage window
  // matches the billing period; on Last 7 / Last 30 / Custom it would
  // be misleading to compare against the monthly allowance.
  const usageAllowance =
    usageRange.preset === 'period' ? tierAllowance : null;

  // Headers for Usage and Transactions cards: in org context include
  // the account name so admins/members reading this know exactly what
  // surface the numbers describe.
  const usageTitle =
    isOrgContext && accountName
      ? t('walletPage.usage.titleInAccount', { accountName })
      : t('walletPage.usage.title');
  const transactionsTitle =
    isOrgContext && accountName
      ? t('walletPage.transactions.titleInAccount', { accountName })
      : t('walletPage.transactions.title');

  const renderAccountTab = () => (
    <div
      role="tabpanel"
      id="wallet-panel-account"
      aria-labelledby="wallet-tab-account"
    >
      <WalletContentTabs
        activeTab={contentTab}
        onChange={handleContentTabChange}
      />

      {/* ---- Summary: balance + plan + usage cards ---- */}
      {contentTab === 'summary' && (
        <div
          role="tabpanel"
          id="wallet-content-panel-summary"
          aria-labelledby="wallet-content-tab-summary"
        >
          {/* Una sola pieza en vez de tres. El saldo, el plan y el consumo
              son la misma resta —5.000 de asignación menos 137 usados son
              4.863 disponibles—, y contarlos en tres tarjetas del mismo peso
              obligaba a hacer la cuenta para saber si se correspondían.
              El pool enterprise se queda aparte: ahí la cuenta no tiene
              saldo propio y lo que hay que decir es de quién es. */}
          {isEnterprisePool ? (
            <div className="wallet-grid wallet-grid--single">
              <BalanceCard variant="managed-by-org" organizationName={orgName} />
            </div>
          ) : (
            <BillingOverviewCard
              totalBalance={balance?.totalBalance}
              monthlyAllowance={tierAllowance}
              totalCharged={usage?.totalCharged}
              planName={tier?.displayName ?? balance?.tier?.displayName}
              anchorDate={balance?.billingCycle?.anchorDate}
              lastResetAt={balance?.billingCycle?.lastResetAt}
              loading={
                (balanceLoading && !balance) ||
                (tierLoading && !tier) ||
                (usageLoading && !usage)
              }
              error={balanceError}
              refreshing={refreshing}
              onRefresh={handleRefresh}
            />
          )}

          {!isEnterprisePool && !usageError && (
            <div className="">
              <WalletSummaryInsights
                totalCharged={usage?.totalCharged ?? 0}
                monthlyAllowance={usageAllowance}
                byResource={usage?.byResource ?? []}
                periodStart={usage?.periodStart}
                anchorDate={balance?.billingCycle?.anchorDate}
                lastResetAt={balance?.billingCycle?.lastResetAt}
                extraBalance={balance?.extraBalance}
                transactions={transactions}
                onViewUsage={() => handleContentTabChange('usage')}
                onViewTransactions={() => handleContentTabChange('transactions')}
              />
            </div>
          )}
        </div>
      )}

      {/* ---- Usage: full per-resource breakdown ---- */}
      {contentTab === 'usage' && (
        <div
          role="tabpanel"
          id="wallet-content-panel-usage"
          aria-labelledby="wallet-content-tab-usage"
          className="wallet-section"
        >
          <UsageSummaryPanel
            data={usage}
            monthlyAllowance={usageAllowance}
            range={usageRange}
            onRangeChange={setUsageRange}
            loading={usageLoading && !usage}
            error={usageError}
            title={usageTitle}
          />
        </div>
      )}

      {/* ---- Transactions: the ledger ---- */}
      {contentTab === 'transactions' && (
        <div
          role="tabpanel"
          id="wallet-content-panel-transactions"
          aria-labelledby="wallet-content-tab-transactions"
          className=""
        >
          <TransactionsTable
            items={transactions}
            loading={transactionsLoading}
            error={transactionsError}
            page={transactionsPage}
            hideZeroAmount={hideZeroAmount}
            onToggleZeroAmount={setHideZeroAmount}
            hasPrevPage={transactionsHasPrev}
            hasNextPage={transactionsHasNext}
            onPrevPage={prevTransactionsPage}
            onNextPage={nextTransactionsPage}
            onRowClick={setOpenTransaction}
            onResetCursor={() => {
              clearTxnFilters();
              refetchTransactions();
            }}
            title={transactionsTitle}
            filters={
              <TransactionFilters
                resourceTypes={txnResourceTypes}
                transactionType={txnTransactionType}
                range={txnRange}
                onResourceTypesChange={setTxnResourceTypes}
                onTransactionTypeChange={setTxnTransactionType}
                onRangeChange={setTxnRange}
                onClearFilters={clearTxnFilters}
              />
            }
          />
        </div>
      )}

      {/* ---- Plan: el tier contratado. Era la entrada "Subscriptions". ---- */}
      {contentTab === 'plan' && (
        <div
          role="tabpanel"
          id="wallet-content-panel-plan"
          aria-labelledby="wallet-content-tab-plan"
          className=""
        >
          <SubscriptionView onManage={() => handleContentTabChange('summary')} />
        </div>
      )}

      {/* ---- Coins: package picker + Tilopay checkout / confirm ---- */}
      {contentTab === 'coins' && (
        <div
          role="tabpanel"
          id="wallet-content-panel-coins"
          aria-labelledby="wallet-content-tab-coins"
          className=""
        >
          <CoinsPurchasePanel accountId={accountId} />
        </div>
      )}
    </div>
  );

  return (
    <div className="wallet-container">
      <header className="wallet-header">
        <div className="wallet-header__titles">
          <h1 className="wallet-title">{t('walletPage.title')}</h1>
          {/* La organización solo se nombra si se llama distinto que la
              cuenta. Con una organización de una sola cuenta las dos llevan
              el mismo nombre, y el subtítulo salía "Sunchild CR · Sunchild
              CR": un separador entre una cosa y ella misma. */}
          {isOrgContext && (accountName || orgName) && (
            <p className="wallet-subtitle">
              {accountName && <span>{accountName}</span>}
              {accountName && orgName && orgName !== accountName && (
                <span className="wallet-subtitle__sep" aria-hidden="true">
                  ·
                </span>
              )}
              {orgName && orgName !== accountName && (
                <span className="wallet-subtitle__org">{orgName}</span>
              )}
            </p>
          )}
        </div>
        <div className="wallet-header__actions">
          {onViewPlans && (
            <button
              type="button"
              className="wallet-action wallet-action--primary"
              onClick={onViewPlans}
            >
              <Crown size={16} aria-hidden="true" />
              {t('walletPage.actions.viewPlans', 'View plans')}
            </button>
          )}
        </div>
      </header>

      {renderAccountTab()}

      <TransactionRowDrawer
        open={Boolean(openTransaction)}
        transaction={openTransaction}
        onClose={() => setOpenTransaction(null)}
      />


    </div>
  );
}